// React Dependencies
import React, { Component } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Slider, Button, withStyles } from '@material-ui/core';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import { Skeleton } from '@material-ui/lab';

// Internal Dependencies
import OverLoading from '../OverLoading';
import Constants from '../../../common/constants';
import { convertDataURLtoBlob } from '../../../common/util';
import CropperForm from './CropperForm';
import './FixedCropperStyles.css'; // Do Not Remove: Overwritten Styles

const createImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', Constants.CrossOrigin);
    image.src = url;
});

const styles = () => ({
  cropper: (props) => ({
    height:                      props.showForm ? '50%' : '100%',
    '@media (min-width: 768px)': {
      height: '100%',
    },
  }),
  skeleton: {
    border:     '2px solid rgba(0, 0, 0, 0.11)',
    position:   'absolute',
    '&::after': {
      background: 'linear-gradient(90deg, transparent, rgba(58, 63, 82, 0.8), transparent)',
    },
  },
  form: {
    paddingBottom:               77,
    '@media (min-width: 768px)': {
      paddingBottom: 0,
    },
  },
});

class FixedCropper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      minZoom:           1,
      zoom:              1,
      type:              props.type,
      label:             props.label,
      fileName:          props.fileName,
      format:            '',
      crop:              { x: 0, y: 0 },
      imageSrc:          props.file ? URL.createObjectURL(props.file) : null,
      imageUrl:          props.url ? props.url : '',
      croppedAreaPixels: null,
      blob:              null,
      photoMinResMet:    false,
      uploading:         false,
      isLoadingImage:    true,
    };
    this.cropWidth = this.props.cropPixelWidth ? this.props.cropPixelWidth : 300;
    this.cropHeight = this.props.cropPixelHeight ? this.props.cropPixelHeight : 300;
    // adjust width to aspect ratio
    // this.cropWidth = this.cropHeight / this.height * this.width;
  }

  componentDidMount() {
    switch (this.state.label) {
      case 'Logo':
        this.setState({ format: 'png' });
        break;
      default:
        // save in jpeg instead of jpg because the file size is smaller for jpeg
        this.setState({ format: 'jpeg' });
    }
  }

  async getCroppedImg(imageBase, pixelCrop) {
    const { label } = this.state;
    const image = await createImage(imageBase);
    const canvas = document.createElement('canvas');
    canvas.width = this.props.outputPixelWidth ? this.props.outputPixelWidth : 300;
    canvas.height = this.props.outputPixelHeight ? this.props.outputPixelHeight : (pixelCrop.height / pixelCrop.width) * 300;

    const ctx = canvas.getContext('2d');

    if (label === 'Logo') {
      ctx.fillStyle = 'rgba(0,0,0,0)';
    } else {
      ctx.fillStyle = '#ffffff';
    }

    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width, // to save image in specific pixel width,
      canvas.height, // to save image in specific pixel height,
    );
    return new Promise((resolve) => {
      // using canvas.toDataUrl instead of canvas.toBlob so that we can adjust
      // the quality to save in smaller file size.
      const imageData = canvas.toDataURL('image/'.concat(this.state.format), 1.0);
      const blob = convertDataURLtoBlob(imageData);
      this.setState({ blob });
      resolve(blob);
    });
  }

  onCropChange = (crop) => {
    this.setState({ crop });
  }

  onCropComplete = (croppedArea, croppedAreaPixels) => {
    this.setState({ croppedAreaPixels });
  }

  onZoomChange = (zoom) => {
    this.setState({ zoom });
  }

  onClose = () => {
    this.props.handleClose();
  }

  saveCroppedImage = async () => {
    try {
      this.setState({ uploading: true });
      const blob = await this.getCroppedImg(this.props.src, this.state.croppedAreaPixels);
      const file = new File([blob || this.state.blob], this.state.fileName ? this.state.fileName : 'image.'.concat(this.state.format),
        { type: 'image/'.concat(this.state.format), lastModified: new Date() });

      const promise = new Promise((resolve, reject) => {
        const save = this.props.index ? this.props.handleSave(file, this.props.index) : this.props.handleSave(file);
        if (save) {
          resolve(save);
        } else {
          reject(new Error('An unexpected error has occurred while saving the image.'));
        }
      });

      promise.then(() => {
        this.setState({ uploading: false });
      }).catch((err) => {
        toast.error(err);
        this.setState({ uploading: false });
      });
    } catch (error) {
      console.log('error: ', error);
      this.setState({ uploading: false });
    }
  }

  render() {
    const { classes } = this.props;
    return (
      <div>
        <div className="crop-container d-block d-md-flex">
          <div
            className={`${this.props.showForm ? 'col-md-6 col-lg-7' : 'col-md-12'} px-0 ${classes.cropper}`}
            style={{ backgroundColor: '#3a3f51' }}
          >
            {
              this.state.isLoadingImage && (
                <div className="w-100 h-100 d-flex justify-content-center align-items-center position-relative">
                  <Skeleton
                    variant="rect"
                    animation="wave"
                    width={this.cropWidth}
                    height={this.cropHeight}
                    className={classes.skeleton}
                  />
                </div>
              )
            }
            <Cropper
              onMediaLoaded={(mediaSize) => {
                const minWidth = this.props.minUploadPixelWidth ? this.props.minUploadPixelWidth : 0;
                const minHeight = this.props.minUploadPixelHeight ? this.props.minUploadPixelHeight : 0;
                this.setState({ photoMinResMet: (mediaSize.naturalWidth >= minWidth && mediaSize.naturalHeight >= minHeight) }, () => {
                  if (!this.state.photoMinResMet) {
                    toast.error('Uploaded image does not meet minimum resolution required');
                  }
                });
                // modify minZoom so that when we zoom out the crop area will still be within the image.
                const minWidthToUploadWidthRatio = this.cropWidth / mediaSize.width;
                const minHeightToUploadHeightRatio = this.cropHeight / mediaSize.height;
                this.setState({
                  minZoom: (minWidthToUploadWidthRatio > minHeightToUploadHeightRatio) ? minWidthToUploadWidthRatio : minHeightToUploadHeightRatio },
                  () => {
                    this.setState((prev) => ({
                      zoom:           prev.minZoom,
                      isLoadingImage: false,
                    }));
                  });
              }}
              image={this.state.type === 'upload' ? this.state.imageSrc : this.state.imageUrl}
              crop={this.state.crop}
              cropShape={this.state.label === 'Logo' ? 'round' : 'rect'}
              minZoom={this.state.minZoom}
              maxZoom={3}
              zoom={this.state.zoom}
              showGrid
              restrictPosition
              aspect={this.props.width / this.props.height}
              onCropChange={this.onCropChange}
              onCropComplete={this.onCropComplete}
              onZoomChange={this.state.uploading ? false : this.onZoomChange}
              style={{
                // mediaStyle: { // this determines the crop pixel size displayed!
                //   minWidth: this.props.cropPixelHeight ? this.props.cropPixelHeight / this.props.height * this.props.width : 300,
                //   minHeight: this.props.cropPixelHeight ? this.props.cropPixelHeight : 300,
                // },
                mediaStyle: {
                  opacity: this.state.isLoadingImage ? 0 : 1,
                },
                containerStyle: {
                  position: 'relative',
                  height:   '100%',
                  width:    '100%',
                },
              }}
              cropSize={{
                width:  this.cropWidth,
                height: this.cropHeight,
              }}
            />
          </div>
          {
            this.props.showForm && (
              <CropperForm className={classes.form} renderForm={this.props.renderForm} />
            )
          }
        </div>
        <div className="fixed-bottom bg-white">
          <div className="controls">
            <Slider
              value={this.state.zoom}
              min={this.state.minZoom}
              max={3}
              step={0.01}
              aria-labelledby="Zoom"
              onChange={(e, zoom) => this.onZoomChange(zoom)}
              disabled={this.state.uploading}
            />
            <Button
              style={{ marginLeft: 50 }}
              onClick={() => { this.saveCroppedImage(); }}
              variant="contained"
              color="primary"
              disabled={this.props.disabled || this.state.uploading || !this.state.photoMinResMet}
            >
              { !this.state.uploading && this.props.isCropping ? 'Crop' : this.props.isCropping && this.state.uploading ? 'Loading' : 'Upload' }
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => this.onClose()}
              disabled={this.state.uploading}
              variant="contained"
              color="primary"
            >
              Cancel
            </Button>
          </div>
        </div>

        { this.state.uploading && <OverLoading /> }
      </div>
    );
  }
}

FixedCropper.propTypes = {
  disabled:             PropTypes.bool,
  type:                 PropTypes.string,
  label:                PropTypes.string,
  fileName:             PropTypes.string,
  file:                 PropTypes.string,
  url:                  PropTypes.string,
  src:                  PropTypes.string,
  cropPixelWidth:       PropTypes.number,
  cropPixelHeight:      PropTypes.number,
  outputPixelWidth:     PropTypes.number,
  outputPixelHeight:    PropTypes.number,
  minUploadPixelWidth:  PropTypes.number,
  minUploadPixelHeight: PropTypes.number,
  width:                PropTypes.number,
  height:               PropTypes.number,
  isCropping:           PropTypes.bool,
  handleClose:          PropTypes.func,
  handleSave:           PropTypes.func,
  index:                PropTypes.number,
  showForm:             PropTypes.bool,
  renderForm:           PropTypes.func,
  classes:              PropTypes.shape({
    cropper:  PropTypes.string,
    form:     PropTypes.string,
    skeleton: PropTypes.string,
  }).isRequired,
};

FixedCropper.defaultProps = {
  disabled:             false,
  type:                 null,
  label:                null,
  fileName:             null,
  file:                 null,
  url:                  null,
  src:                  null,
  cropPixelWidth:       null,
  cropPixelHeight:      null,
  outputPixelWidth:     null,
  outputPixelHeight:    null,
  minUploadPixelWidth:  null,
  minUploadPixelHeight: null,
  width:                null,
  height:               null,
  isCropping:           false,
  index:                null,
  showForm:             false,
  renderForm:           () => {},
  handleClose:          () => {},
  handleSave:           () => {},
};

export default withStyles(styles)(FixedCropper);
